import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useStorefrontTenantSettings } from './useStorefrontTenantSettings'

function Row({
  label,
  value,
  isLoading,
}: {
  label: string
  value: React.ReactNode
  isLoading: boolean
}) {
  return (
    <div className="flex items-center justify-between px-4 py-4">
      <span className="text-sm font-medium">{label}</span>
      {isLoading ? (
        <Skeleton className="w-24 h-4 rounded-md" />
      ) : (
        <span className="text-sm text-muted-foreground">{value}</span>
      )}
    </div>
  )
}

function Divider() {
  return <div className="border-t border-border mx-4" />
}

const baht = (n?: number) =>
  n != null ? `${n.toLocaleString()} THB` : '—'
const kg = (n?: number) => (n != null ? `${n} kg` : '—')

/**
 * Read-only view of the chargeable-weight shipping config (Storefront Tenant
 * custom_shipping_* fields). Only renders when the tenant has the model enabled,
 * so non-chargeable tenants see nothing. Values are edited in Frappe Desk for now.
 */
export function ChargeableShippingSettings() {
  const { settings, isLoading } = useStorefrontTenantSettings()
  const enabled = !!settings?.custom_shipping_chargeable_enabled

  // Hide entirely for tenants not using chargeable-weight shipping.
  if (!isLoading && !enabled) return null

  return (
    <Card className="rounded-2xl border-0">
      <CardContent className="p-0">
        <div className="px-4 pt-5 pb-2">
          <h3 className="text-base font-semibold">Chargeable-Weight Shipping</h3>
          <p className="text-xs text-muted-foreground">
            Fee uses chargeable weight (the greater of gross vs volumetric) against a
            value-by-weight rate table. Read-only here — edit in Frappe Desk.
          </p>
        </div>
        <Row label="Status" isLoading={isLoading} value={enabled ? 'Enabled' : 'Disabled'} />
        <Divider />
        <Row label="Flat rate (light parcels)" isLoading={isLoading} value={baht(settings?.custom_shipping_flat_rate)} />
        <Divider />
        <Row label="Rate per kg (under value threshold)" isLoading={isLoading} value={baht(settings?.custom_shipping_rate_under)} />
        <Divider />
        <Row label="Rate per kg (over value threshold)" isLoading={isLoading} value={baht(settings?.custom_shipping_rate_over)} />
        <Divider />
        <Row label="Free-tier order value" isLoading={isLoading} value={baht(settings?.custom_shipping_value_threshold)} />
        <Divider />
        <Row label="Free weight (above threshold)" isLoading={isLoading} value={kg(settings?.custom_shipping_free_weight_kg)} />
        <Divider />
        <Row label="Volumetric divisor" isLoading={isLoading} value={settings?.custom_shipping_volumetric_divisor ?? '—'} />
        <Divider />
        <Row label="Default weight unit" isLoading={isLoading} value={settings?.custom_shipping_default_weight_uom || '—'} />
        <Divider />
        <Row label="Income account" isLoading={isLoading} value={settings?.custom_shipping_income_account || '—'} />
      </CardContent>
    </Card>
  )
}
